import {
  bookingDraftKey,
  useBookingStore,
} from '@/features/public/booking/context/bookingStore.tsx';
// Typ, nie moduł: `bookingRoutes.ts` ciągnie za sobą `queryClient` z `App.tsx`,
// a `import type` znika przy transpilacji.
import type { BookingView } from '@/features/public/booking/routes/bookingRoutes.ts';
import { FunnelEventStage } from '@/features/public/booking/tracking/FunnelEventStage.ts';
import { fetchPublicApi } from '@/lib/api/apiServiceBase.ts';

const sentStagesKey = (tourKey: string) => `campdesk-funnel:${tourKey}`;

/**
 * Etapy wysłane w tej wizycie. Dostępy do `sessionStorage` są w `try/catch`,
 * bo prywatne okno Safari rzuca przy zapisie — statystyka nie ma prawa
 * wywrócić strony rezerwacji.
 */
const sentStages = (tourKey: string): string[] => {
  try {
    const raw = sessionStorage.getItem(sentStagesKey(tourKey));
    // Wpis mógł zostać podmieniony na cokolwiek — z liczby czy obiektu
    // `includes` zrobiłoby `TypeError` po stronie wołającego.
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const markSent = (tourKey: string, stage: FunnelEventStage): void => {
  try {
    sessionStorage.setItem(
      sentStagesKey(tourKey),
      JSON.stringify([...sentStages(tourKey), stage]),
    );
  } catch {
    // Zapis niemożliwy — wyślemy etap ponownie przy następnym wejściu,
    // a `ON CONFLICT DO NOTHING` po stronie backendu i tak go odbije.
  }
};

/**
 * Zdarzenie lejka. Nigdy nie blokuje UI i nigdy nie rzuca: statystyka nie ma
 * prawa zepsuć rezerwacji. Duplikaty odsiewa `sessionStorage`, a te, które
 * przejdą, odbija `ON CONFLICT DO NOTHING` po stronie backendu.
 *
 * Odpowiedź to `204`: `fetchApi` zwraca `null`, bo nie ma nagłówka
 * `Content-Type` — niczego tu nie czytamy.
 */
export const trackFunnelStage = (
  orgSlug: string,
  tourSlug: string,
  stage: FunnelEventStage,
): void => {
  const tourKey = bookingDraftKey(orgSlug, tourSlug);
  if (sentStages(tourKey).includes(stage)) return;

  // Znacznik idzie przed żądaniem: dwa wywołania w tym samym ticku (StrictMode,
  // powtórzony `beforeLoad`) mają wysłać jedno żądanie, nie dwa. Ceną jest brak
  // ponowienia — żądanie, które padło, nie wróci już w tej sesji karty.
  markSent(tourKey, stage);

  // Wołamy z `beforeLoad` i z `loader`, gdzie rzucenie zamienia ofertę na stronę
  // błędu. Sam identyfikator ma dwie drogi wyjścia: `crypto.randomUUID` nie
  // istnieje poza bezpiecznym kontekstem, a zapis szkicu idzie przez `persist`
  // do `localStorage`, który w prywatnym oknie rzuca przy przekroczeniu limitu.
  try {
    const sessionId = useBookingStore.getState().ensureFunnelSessionId(tourKey);

    void fetchPublicApi(
      'POST',
      `public/organizers/${orgSlug}/tours/${tourSlug}/funnel-events`,
      { sessionId, stage },
      { keepalive: true },
    ).catch(() => {});
  } catch {
    return;
  }
};

/**
 * Który etap lejka odpowiada rozstrzygniętemu widokowi oferty.
 *
 * Komplet miejsc jedzie tym samym etapem niezależnie od tego, czy jest lista
 * rezerwowa — inaczej wyjazdy z listą wypadłyby z licznika, bo `beforeLoad`
 * przekierowuje je, zanim ktokolwiek zobaczy stronę oferty.
 *
 * Bez `default`: dołożenie widoku ma wywalić kompilację, a nie po cichu
 * przestać się liczyć.
 */
export const bookingViewStage = (
  view: BookingView,
): FunnelEventStage | null => {
  switch (view.type) {
    case 'booking':
      return FunnelEventStage.OFFER_VIEWED;
    case 'not-open':
      return FunnelEventStage.OFFER_VIEWED_NOT_OPEN;
    case 'registration-closed':
      return FunnelEventStage.OFFER_VIEWED_CLOSED;
    case 'sold-out':
    case 'waitlist':
      return FunnelEventStage.OFFER_VIEWED_SOLD_OUT;
    case 'tour-cancelled':
      return null;
  }
};
